import {
    useEffect,
    useImperativeHandle,
    forwardRef,
    useRef,
    useState,
    useCallback,
    useMemo,
    type ReactNode,
} from 'react';
import { parseCookies } from 'nookies';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';
import ReaderPageImage from './ReaderPageImage';
import { fetchChapterPagesArray, pageEntryToSrc } from './chapter-cdn-pages';
import { api } from '@/utils/api';
import ChapterInteractions from './ChapterInteractions';

import 'swiper/css';

const markedSession = new Set<string>();

export type HorizontalReaderHandle = {
    slidePrev: () => void;
    slideNext: () => void;
    slideTo: (index: number) => void;
};

interface HorizontalReaderProps {
    capitulo: any;
    capitulos?: any[];
    imageZoom?: number;
    imageBrightness?: number;
    infiniteScroll?: boolean;
    /** Há próximo capítulo na obra (para slide extra em leitura infinita). */
    hasNextChapterInObra?: boolean;
    /** Enquanto o próximo capítulo está sendo buscado. */
    loadingAppendNext?: boolean;
    /** Nome do próximo capítulo ainda não carregado (slide final). */
    nextChapterName?: string | null;
    /** Ao entrar no slide “próximo capítulo” (dispara pré-carregamento). */
    onRequestLoadNextChapter?: () => void;
    onPageChange?: (current: number, total: number) => void;
    onActiveChapterChange?: (capId: number) => void;
    /** Antes das reações/comentários (ex.: navegação entre capítulos). */
    beforeInteractions?: ReactNode;
}

type FlatPage = {
    kind: 'page';
    capId: number;
    chapter: any;
    indexInChapter: number;
    totalInChapter: number;
    src: string | null;
};

type FlatSlide = FlatPage;

const HorizontalReader = forwardRef<HorizontalReaderHandle, HorizontalReaderProps>(function HorizontalReader(
    {
        capitulo,
        capitulos,
        imageZoom = 100,
        imageBrightness = 100,
        infiniteScroll = false,
        hasNextChapterInObra = false,
        loadingAppendNext = false,
        nextChapterName = null,
        onRequestLoadNextChapter,
        onPageChange,
        onActiveChapterChange,
        beforeInteractions = null,
    },
    ref
) {
    const [flatSlides, setFlatSlides] = useState<FlatSlide[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const capId = String(capitulo?.cap_id ?? capitulo?.id ?? '');
    const chapterList = useMemo(() => {
        if (Array.isArray(capitulos) && capitulos.length > 0) return capitulos;
        return capitulo ? [capitulo] : [];
    }, [capitulos, capitulo]);
    const chapterIdsKey = useMemo(
        () => chapterList.map((c) => String(c?.cap_id ?? c?.id ?? '')).join(','),
        [chapterList]
    );
    const swiperRef = useRef<SwiperType | null>(null);
    const chapterPagesCacheRef = useRef<Map<number, any[]>>(new Map());
    const appendRequestedRef = useRef(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const hasLoadedOnceRef = useRef(false);
    const rangesRef = useRef<Map<number, { start: number; end: number; total: number; chapter: any }>>(new Map());

    const contentCount = flatSlides.length;
    const showNextChapterSlide = Boolean(infiniteScroll && hasNextChapterInObra && contentCount > 0);
    const totalSlides = contentCount + (showNextChapterSlide ? 1 : 0);

    const emitPage = useCallback(
        (activeIndex: number) => {
            setActiveIdx(activeIndex);
            const contentPages = Math.max(1, contentCount);
            const isOnAppendSlide = showNextChapterSlide && activeIndex >= contentCount;
            const displayCurrent = isOnAppendSlide ? contentPages + 1 : Math.min(activeIndex + 1, contentPages);
            const displayTotal = showNextChapterSlide ? contentPages + 1 : contentPages;
            onPageChange?.(displayCurrent, displayTotal);
        },
        [contentCount, onPageChange, showNextChapterSlide]
    );

    async function markChapterAsRead(chapter: any) {
        const localCapId = String(chapter?.cap_id ?? chapter?.id ?? '');
        if (!localCapId || markedSession.has(localCapId)) return;
        const token = parseCookies()['token'];
        if (!token) return;
        const cap_id = Number(chapter?.cap_id ?? chapter?.id);
        const obr_id = Number(chapter?.obra?.id ?? chapter?.obra?.obr_id ?? chapter?.obr_id);
        if (!Number.isInteger(cap_id) || cap_id < 1 || !Number.isInteger(obr_id) || obr_id < 1) return;
        try {
            await api.post('readings/mark', { obr_id, cap_id });
            markedSession.add(localCapId);
        } catch {
            /* silencioso */
        }
    }

    useEffect(() => {
        if (chapterList.length === 0) {
            setFlatSlides([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            if (!hasLoadedOnceRef.current) setLoading(true);
            setError(null);
            try {
                const pagesByChapter: Array<{ chapter: any; pages: any[] }> = [];
                for (const chapter of chapterList) {
                    const idNum = Number(chapter?.cap_id ?? chapter?.id);
                    if (!Number.isFinite(idNum) || idNum <= 0) continue;
                    let pages = chapterPagesCacheRef.current.get(idNum);
                    if (!pages) {
                        pages = await fetchChapterPagesArray(chapter);
                        chapterPagesCacheRef.current.set(idNum, pages);
                    }
                    pagesByChapter.push({ chapter, pages });
                }
                if (cancelled) return;

                let cursor = 0;
                const ranges = new Map<number, { start: number; end: number; total: number; chapter: any }>();
                const merged: FlatSlide[] = [];
                for (let i = 0; i < pagesByChapter.length; i += 1) {
                    const { chapter, pages } = pagesByChapter[i];
                    const idNum = Number(chapter?.cap_id ?? chapter?.id);
                    const total = pages.length;
                    if (total > 0) {
                        ranges.set(idNum, { start: cursor, end: cursor + total - 1, total, chapter });
                    }
                    pages.forEach((p, idx) => {
                        merged.push({
                            kind: 'page',
                            capId: idNum,
                            chapter,
                            indexInChapter: idx,
                            totalInChapter: total,
                            src: pageEntryToSrc(p),
                        });
                    });
                    cursor += total;
                }

                rangesRef.current = ranges;
                setFlatSlides(merged);
                hasLoadedOnceRef.current = true;
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'Erro ao carregar páginas');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 100);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [chapterList, chapterIdsKey]);

    useEffect(() => {
        appendRequestedRef.current = false;
    }, [chapterIdsKey, showNextChapterSlide]);

    useImperativeHandle(
        ref,
        () => ({
            slidePrev: () => swiperRef.current?.slidePrev(),
            slideNext: () => swiperRef.current?.slideNext(),
            slideTo: (index: number) => swiperRef.current?.slideTo(index),
        }),
        []
    );

    const handleSlideChange = (swiper: SwiperType) => {
        const idx = swiper.activeIndex;
        emitPage(idx);

        if (showNextChapterSlide && idx >= contentCount && contentCount > 0) {
            if (!appendRequestedRef.current) {
                appendRequestedRef.current = true;
                onRequestLoadNextChapter?.();
            }
        }

        if (idx >= 0 && idx < contentCount) {
            const current = flatSlides[idx];
            if (current?.kind === 'page') {
                onActiveChapterChange?.(current.capId);
                const range = rangesRef.current.get(current.capId);
                if (range) {
                    const localIndex = idx - range.start;
                    if (localIndex >= Math.max(0, Math.ceil(range.total / 2) - 1)) {
                        void markChapterAsRead(range.chapter);
                    }
                }
            }
        }
    };

    if (loading) {
        return (
            <div className="w-full py-24 flex items-center justify-center min-h-[50vh]">
                <div className="w-10 h-10 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full py-16 px-4 text-center text-red-400/90 text-sm">
                {error}
            </div>
        );
    }

    if (flatSlides.length === 0) {
        return (
            <div className="w-full py-16 px-4 text-center text-white/50 text-sm">
                Nenhuma página encontrada.
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col">
            <div className="w-full relative min-h-[min(85vh,800px)]">
                <Swiper
                    modules={[Keyboard, Mousewheel]}
                    onSwiper={(sw) => {
                        swiperRef.current = sw;
                        emitPage(sw.activeIndex);
                    }}
                    onSlideChange={handleSlideChange}
                    keyboard={{ enabled: true }}
                    mousewheel={{ forceToAxis: true, sensitivity: 0.8 }}
                    className="w-full horizontal-manga-swiper"
                    grabCursor
                    spaceBetween={0}
                    slidesPerView={1}
                >
                    {flatSlides.map((slide, index) => {
                        const url = slide.src;
                        return (
                            <SwiperSlide key={`${slide.capId}-${index}`} className="!flex items-center justify-center">
                                <div className="w-full min-h-[min(85vh,800px)] flex items-center justify-center px-1">
                                    {url ? (
                                        <ReaderPageImage
                                            src={url}
                                            alt={`Página ${slide.indexInChapter + 1}`}
                                            fetchPriority={index < 2 ? 'high' : 'auto'}
                                            zoomPercent={imageZoom}
                                            brightnessPercent={imageBrightness}
                                        />
                                    ) : (
                                        <div className="w-full min-h-[200px] flex items-center justify-center text-white/40 text-xs">
                                            Página {slide.indexInChapter + 1} inválida
                                        </div>
                                    )}
                                </div>
                            </SwiperSlide>
                        );
                    })}

                    {showNextChapterSlide && (
                        <SwiperSlide key={`${capId}-next`} className="!flex items-center justify-center">
                            <div className="w-full min-h-[min(85vh,800px)] flex flex-col items-center justify-center gap-4 px-6 text-center border-t border-white/10 bg-zinc-950/80">
                                <BookOpen className="text-orange-400/90" size={40} />
                                <p className="text-white/95 text-lg md:text-xl font-semibold leading-snug max-w-xl">
                                    {nextChapterName && nextChapterName.trim() ? nextChapterName : 'Próximo capítulo'}
                                </p>
                                <p className="text-white/50 text-sm max-w-sm">
                                    {loadingAppendNext
                                        ? 'Carregando…'
                                        : 'Continue a leitura em seguida ou deslize de volta para revisar as páginas.'}
                                </p>
                                {loadingAppendNext && (
                                    <span className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
                                )}
                            </div>
                        </SwiperSlide>
                    )}
                </Swiper>
            </div>

            {/* Controles locais (redundantes com a barra fixa; úteis em desktop) */}
            <div className="flex items-center justify-center gap-3 py-3 md:py-4 border-t border-white/5">
                <button
                    type="button"
                    onClick={() => swiperRef.current?.slidePrev()}
                    className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/10"
                    aria-label="Página anterior"
                >
                    <ChevronLeft size={22} />
                </button>
                <span className="text-xs font-mono text-white/60 tabular-nums min-w-[5rem] text-center">
                    {activeIdx >= contentCount ? contentCount + 1 : activeIdx + 1} / {totalSlides}
                </span>
                <button
                    type="button"
                    onClick={() => swiperRef.current?.slideNext()}
                    className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/10"
                    aria-label="Próxima página"
                >
                    <ChevronRight size={22} />
                </button>
            </div>

            {beforeInteractions}

            <ChapterInteractions
                capitulo={
                    flatSlides[Math.min(activeIdx, Math.max(0, contentCount - 1))]?.chapter ?? capitulo
                }
                infiniteScroll={infiniteScroll}
            />
        </div>
    );
});

export default HorizontalReader;
